import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  BookOpen, Users, Settings2, TrendingUp,
  ArrowUpRight, GraduationCap, Download,
} from "lucide-react";
import "./IncubationBanner.scss";
import cogradLogo from "../assets/logo-white.webp";
import flipImg1 from "../assets/key-offerings/sip-1.jpg";
import flipImg2 from "../assets/key-offerings/sip-2.jpg";
import flipImg3 from "../assets/key-offerings/sip-3.jpg";
import flipImg4 from "../assets/key-offerings/sip-4.jpg";
import AsOfNote from "./AsOfNote";
import { impact } from "./../data/stats";

const STATS = [
  { value: impact.students.display,  label: impact.students.label  },
  { value: impact.schools.display,   label: impact.schools.label   },
  { value: impact.incubated.display, label: impact.incubated.label },
  { value: "2026",                   label: "Cohort Now Open"      },
];

const FEATURES = [
  {
    icon: BookOpen,
    title: "Academic Transformation",
    desc: "Structured lesson planning, academic calendars & consistent classroom performance tracking.",
    image: flipImg1,
  },
  {
    icon: Users,
    title: "Teacher & Student Growth",
    desc: "Expert-led training sessions, real-world exposure & skill development programs.",
    image: flipImg2,
  },
  {
    icon: Settings2,
    title: "School Systems & SOPs",
    desc: "Consistent execution processes, leadership training & principal accountability.",
    image: flipImg3,
  },
  {
    icon: TrendingUp,
    title: "Admissions & Growth",
    desc: "Data-driven insights, monthly performance tracking & structured growth strategy.",
    image: flipImg4,
  },
];

export default function IncubationBanner() {
  const [blueFlipped, setBlueFlipped] = useState(false);
  const [flippedCard, setFlippedCard] = useState(null);

  return (
    <section className="ib-wrap">
      <div className="ib-inner">

        {/* ── Header ── */}
        <motion.div
          className="ib__header"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <div className="ib__chip">
            <GraduationCap size={12} strokeWidth={2.5} /> SCHOOL INCUBATION PROGRAM
          </div>
          <h2 className="ib__h2">
            Transform Your School into a <span>High-Performance Institution</span>
          </h2>
        </motion.div>

        {/* ── Bento grid ── */}
        <motion.div
          className="ib__bento"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, ease: "easeOut", delay: 0.08 }}
        >
          {/* LEFT — blue card spanning 2 rows (flip on hover) */}
          <div
            className={`ib__blue-flip${blueFlipped ? " is-flipped" : ""}`}
            onClick={(e) => {
              if (!e.target.closest(".ib__apply-btn")) {
                setBlueFlipped((f) => !f);
              }
            }}
          >
            <div className="ib__blue-inner">

              {/* Front */}
              <div className="ib__blue-card ib__blue-front">
                <div className="ib__blue-card-deco" />
                <div className="ib__blue-ico">
                  <GraduationCap size={26} strokeWidth={1.8} />
                </div>
                <h3 className="ib__blue-title">CoGrad School<br />Incubation</h3>
                <p className="ib__blue-desc">
                  We don't offer advice. We embed inside schools and rebuild them
                  end-to-end — through systems, not theory.
                </p>
                <div className="ib__stat-grid">
                  {STATS.map((s) => (
                    <div className="ib__stat-box" key={s.label}>
                      <span className="ib__stat-val">{s.value}</span>
                      <span className="ib__stat-lbl">{s.label}</span>
                    </div>
                  ))}
                </div>
                <AsOfNote className="asof--center ib__asof" />
                <a href="/incubation" className="ib__apply-btn">
                  Apply for Selection <ArrowUpRight size={15} strokeWidth={2.5} />
                </a>
                <div className="ib__flip-hint">
                  {blueFlipped ? "Tap to go back ←" : "Tap / hover to view brochure →"}
                </div>
              </div>

              {/* Back — live brochure embed */}
              <div className="ib__blue-back">
                <div className="ib__brochure-header">
                  <img src={cogradLogo} alt="CoGrad" className="ib__brochure-logo" />
                  <a href="https://www.canva.com/design/DAHGjiyyeAE/OxD1NFDGbWqd2GXxAoLExg/view" className="ib__brochure-dl" target="_blank" rel="noopener noreferrer">
                    <Download size={13} /> Download
                  </a>
                </div>
                <iframe
                  className="ib__brochure-frame"
                  src="https://www.canva.com/design/DAHGjiyyeAE/OxD1NFDGbWqd2GXxAoLExg/view?embed"
                  title="CoGrad Incubation Brochure"
                  allowFullScreen
                  allow="fullscreen"
                  loading="lazy"
                />
              </div>

            </div>
          </div>

          {/* RIGHT — 4 flip feature cards */}
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                className="ib__feat-card"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.4,
                  ease: "easeOut",
                  delay: 0.12 + i * 0.07,
                }}
              >
                {/* Separate flip container from framer-motion wrapper */}
                <div
                  className={`ib__feat-flip${flippedCard === i ? " is-flipped" : ""}`}
                  onClick={() => setFlippedCard(flippedCard === i ? null : i)}
                >
                  <div className="ib__feat-inner">
                    {/* Front */}
                    <div className="ib__feat-front">
                      <div className="ib__feat-ico">
                        <Icon size={20} strokeWidth={1.8} />
                      </div>
                      <h4 className="ib__feat-title">{f.title}</h4>
                      <p className="ib__feat-desc">{f.desc}</p>
                    </div>

                    {/* Back */}
                    <div className="ib__feat-back">
                      <img src={f.image} alt={f.title} />
                      <div className="ib__feat-back-body">
                        <h4 className="ib__feat-back-title">{f.title}</h4>
                        {f.brochure && (
                          <a href="/incubation" className="ib__feat-brochure-btn">
                            <Download size={12} strokeWidth={2.5} />
                            Download Brochure
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* ── Urgency ── */}
        <motion.div
          className="ib__urgency"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <span className="ib__urgency-text">
            <span className="ib__urgency-dot" />
            Seats are limited — apply early.
          </span>
          <a href="/incubation" className="ib__urgency-link">Apply now →</a>
        </motion.div>

      </div>
    </section>
  );
}
